import { BaseFeature } from '../../core/base-feature.js';

/**
 * InputSabotageFeature - Frontend feature for controlling input sabotage
 *
 * Controls mouse, keyboard, and clipboard sabotage effects on the desktop.
 */
export class InputSabotageFeature extends BaseFeature {
    constructor(coreAPI) {
        super(coreAPI);

        // State
        this.enabled = false;
        this.effects = {};
        this.activeCount = 0;
        this.isLoading = false;

        // Event listeners for React components
        this.updateCallbacks = new Set();

        // Pending promises for request/response
        this._pendingRequests = new Map();
    }

    static getMetadata() {
        return {
            id: 'input-sabotage',
            name: 'input-sabotage',
            version: 'v1.0',
            capabilities: [
                'set_enabled',
                'toggle_effect',
                'configure_effect',
                'get_state'
            ],
            dependencies: []
        };
    }

    async initialize() {
        console.log('[InputSabotage] Initializing...');
        console.log('[InputSabotage] Initialized');
    }

    async handleMessage(message) {
        const { action, payload, messageId, fromId } = message;

        switch (action) {
            case 'set_enabled_response':
                return this.handleSetEnabledResponse(payload);

            case 'toggle_effect_response':
                return this.handleToggleEffectResponse(payload);

            case 'configure_effect_response':
                return this.handleConfigureEffectResponse(payload);

            case 'get_state_response':
                return this.handleGetStateResponse(payload);

            case 'state_update':
                return this.handleStateUpdate(payload);

            default:
                console.warn(`[InputSabotage] Unknown action: ${action}`);
        }
    }

    // =====================================
    // Commands
    // =====================================

    /**
     * Enable or disable input sabotage master switch
     * @param {boolean} enabled - Whether to enable sabotage
     * @returns {Promise<Object>} Result
     */
    async setEnabled(enabled) {
        console.log(`[InputSabotage] Setting enabled: ${enabled}`);

        return new Promise((resolve) => {
            this.sendMessage('set_enabled', { enabled });
            this._pendingRequests.set('set_enabled', resolve);
        });
    }

    handleSetEnabledResponse(payload) {
        const resolve = this._pendingRequests.get('set_enabled');
        if (resolve) {
            this._pendingRequests.delete('set_enabled');
            resolve(payload);
        }

        if (payload.success) {
            this.enabled = payload.enabled;
            this.notifyUpdateListeners();
        }
    }

    /**
     * Toggle an individual effect
     * @param {string} effectId - Effect identifier
     * @param {boolean} enabled - Whether to enable the effect
     * @returns {Promise<Object>} Result
     */
    async toggleEffect(effectId, enabled) {
        console.log(`[InputSabotage] Toggling effect: ${effectId} -> ${enabled}`);

        return new Promise((resolve) => {
            this.sendMessage('toggle_effect', { effectId, enabled });
            this._pendingRequests.set(`toggle_${effectId}`, resolve);
        });
    }

    handleToggleEffectResponse(payload) {
        const resolve = this._pendingRequests.get(`toggle_${payload.effectId}`);
        if (resolve) {
            this._pendingRequests.delete(`toggle_${payload.effectId}`);
            resolve(payload);
        }

        if (payload.success) {
            this.notifyUpdateListeners();
        }
    }

    /**
     * Configure an effect
     * @param {string} effectId - Effect identifier
     * @param {Object} config - Configuration options
     * @returns {Promise<Object>} Result
     */
    async configureEffect(effectId, config) {
        console.log(`[InputSabotage] Configuring effect: ${effectId}`, config);

        return new Promise((resolve) => {
            this.sendMessage('configure_effect', { effectId, config });
            this._pendingRequests.set(`configure_${effectId}`, resolve);
        });
    }

    handleConfigureEffectResponse(payload) {
        const resolve = this._pendingRequests.get(`configure_${payload.effectId}`);
        if (resolve) {
            this._pendingRequests.delete(`configure_${payload.effectId}`);
            resolve(payload);
        }

        if (payload.success) {
            this.notifyUpdateListeners();
        }
    }

    /**
     * Get current state
     * @returns {Promise<Object>} Current state
     */
    async getState() {
        console.log('[InputSabotage] Requesting current state');

        return new Promise((resolve) => {
            this.sendMessage('get_state', {});
            this._pendingRequests.set('get_state', resolve);
        });
    }

    handleGetStateResponse(payload) {
        const resolve = this._pendingRequests.get('get_state');
        if (resolve) {
            this._pendingRequests.delete('get_state');
            resolve(payload);
        }

        if (payload.success && payload.state) {
            this.enabled = payload.state.enabled;
            this.effects = payload.state.effects;
            this.activeCount = payload.state.activeCount;
            this.notifyUpdateListeners();
        }
    }

    // =====================================
    // State Updates
    // =====================================

    handleStateUpdate(payload) {
        console.log('[InputSabotage] State update received', payload);

        this.enabled = payload.enabled;
        this.effects = payload.effects;
        this.activeCount = payload.activeCount;

        this.notifyUpdateListeners();
    }

    // =====================================
    // Event Listeners
    // =====================================

    /**
     * Subscribe to update events
     * @param {Function} callback - Called when state changes
     * @returns {Function} Unsubscribe function
     */
    onUpdate(callback) {
        this.updateCallbacks.add(callback);
        return () => this.updateCallbacks.delete(callback);
    }

    notifyUpdateListeners() {
        const data = {
            enabled: this.enabled,
            effects: this.effects,
            activeCount: this.activeCount,
            isLoading: this.isLoading
        };

        for (const callback of this.updateCallbacks) {
            try {
                callback(data);
            } catch (error) {
                console.error('[InputSabotage] Error in update callback:', error);
            }
        }
    }

    // =====================================
    // Getters
    // =====================================

    getEnabled() {
        return this.enabled;
    }

    getEffects() {
        return { ...this.effects };
    }

    getActiveCount() {
        return this.activeCount;
    }

    getIsLoading() {
        return this.isLoading;
    }

    // =====================================
    // Cleanup
    // =====================================

    async cleanup() {
        console.log('[InputSabotage] Cleaning up...');
        this.enabled = false;
        this.effects = {};
        this.activeCount = 0;
        this.updateCallbacks.clear();
        this._pendingRequests.clear();
    }
}
